import React, { FC, useEffect, useRef, useState } from 'react';
import { Filter } from '$shared/utils/filter/components';

import { useProduct } from '$shared/utils/product';
import { useFilterQuery } from '$shared/utils/filter/service/useFilterQuery';
import { ModuleContainer } from "$templates/blocks/module-container";
import { FilterProductList } from "$templates/blocks/m170-filter/components/filter-product-list";
import { DynamicFacets } from "$templates/blocks/m170-filter/components/dynamic-facets";
import {
    ProductFilterGrid,
    StyledFilterColumn,
    StyledProductColumn,
} from "$templates/blocks/m170-filter/style";
import ResetFilterButton from "$shared/components/facets/reset-filter-button/facet-search-bar";
import { FILTER_HOLDER_KEY, FILTER_INSERT_KEY } from "$utils/filter/service/constants";
import {
    parseProductContent
} from "$templates/pages/components/P50ProductDetailPage/components/pdp-tabs/pdp-relationships/product-data-parser";
import {IContentData} from "~/lib/data-contract";

export const PdpRelationshipsFilter: FC<{ relationshipKey: string }> = ({ relationshipKey }) => {
    const { productData, originalItemNumber } = useProduct();
    const [loading, setLoading] = useState(false);
    const appliedFiltersRef = useRef<{ [key: string]: string[] }>({});
    const [facetValues, setFacetValues] = useState<string[]>([]);
    const prevFacetValuesRef = useRef<string[]>([]);

    useEffect(() => {
        if (productData) {
            setLoading(true);
            setFacetValues([]);

            const parsedProductData = productData.map(product => ({
                ...product,
                parsedContent: parseProductContent(product.content),
            }));

            const productWithKey = parsedProductData.find(
                (product) =>
                    product.parsedContent &&
                    typeof product.parsedContent === 'object' &&
                    relationshipKey in product.parsedContent
            );

            const parsedContent = productWithKey?.parsedContent as { [key: string]: IContentData[] };
            if (parsedContent?.[relationshipKey] && Array.isArray(parsedContent[relationshipKey])) {
                const computedValues = parsedContent[relationshipKey].flatMap((entry) => entry.values || []);
                setFacetValues(computedValues);
            } else {
                setFacetValues([]);
            }

            setLoading(false);
        }
    }, [productData, relationshipKey]);

    return (
        <ModuleContainer id={`relationships-${originalItemNumber}`} fullWidth>
            <div>
                <h2>
                    {relationshipKey === FILTER_HOLDER_KEY ? "Holder Keys" : relationshipKey === FILTER_INSERT_KEY ? "Insert Keys" : ""}:{" "}
                    {facetValues.length ? facetValues.join(", ") : "No values found"}
                </h2>
            </div>

            <Filter
                filterDataExchanger={useFilterQuery}
                filterScopeId={originalItemNumber || ""}
            >
                {({ resetFilter, setFacets }) => {
                    const [didReset, setDidReset] = useState(false);
                    const [didSetFacets, setDidSetFacets] = useState(false);

                    useEffect(() => {
                        setDidReset(false);
                        setDidSetFacets(false);
                    }, [relationshipKey]);

                    useEffect(() => {
                        if (facetValues.length === 0) return;
                        const searchKey = relationshipKey === FILTER_HOLDER_KEY ? FILTER_INSERT_KEY : FILTER_HOLDER_KEY;

                        if (!didReset) {

                            resetFilter && resetFilter();
                            setDidReset(true);
                            return;
                        }
                        const facetValuesChanged = JSON.stringify(prevFacetValuesRef.current) !== JSON.stringify(facetValues);

                        if (!didSetFacets || facetValuesChanged) {
                            const facetOptions = facetValues.map((value) => `${searchKey}~${value}`);
                            setFacets &&
                            setFacets([
                                {
                                    facet: searchKey,
                                    options: facetOptions,
                                    overwrite: false,
                                },
                            ]);
                            appliedFiltersRef.current[searchKey] = facetOptions;
                            setDidSetFacets(true);
                            prevFacetValuesRef.current = facetValues;
                        }
                    }, [relationshipKey, facetValues, didReset, didSetFacets, resetFilter, setFacets]);

                    return (
                        <ProductFilterGrid>
                            <StyledFilterColumn>
                                <ResetFilterButton handleClick={() => {
                                    if (resetFilter) {
                                        setDidReset(false);
                                        setDidSetFacets(false);
                                        resetFilter();
                                    }
                                }} />
                                <DynamicFacets />
                            </StyledFilterColumn>

                            <StyledProductColumn>
                                {loading ? <p>{"Loading"}</p> : <FilterProductList id={originalItemNumber || ""} />}
                            </StyledProductColumn>
                        </ProductFilterGrid>
                    );
                }}
            </Filter>
        </ModuleContainer>
    );
};